import { Injectable } from '@angular/core';

/**
 * Service for managing session-scoped correlation IDs.
 *
 * Correlation IDs are used to link all logs from a single user session,
 * enabling log aggregation and tracing across the entire payment flow.
 *
 * The correlation ID persists for the browser session (sessionStorage)
 * and is regenerated when the user opens a new browser session.
 */
@Injectable({
  providedIn: 'root',
})
export class CorrelationIdService {
  private readonly STORAGE_KEY = 'dp_correlation_id';

  /**
   * Get the current session correlation ID.
   * If none exists, generates a new one and stores it in sessionStorage.
   *
   * @returns The correlation ID for the current session
   */
  getCorrelationId(): string {
    // Check for existing correlation ID in sessionStorage
    let correlationId = sessionStorage.getItem(this.STORAGE_KEY);

    if (!correlationId) {
      // Generate new UUID for this session
      correlationId = this.generateUUID();
      sessionStorage.setItem(this.STORAGE_KEY, correlationId);
    }

    return correlationId;
  }

  /**
   * Regenerate the correlation ID.
   * Use this when starting a new logical session (e.g., after logout or starting a new payment flow).
   *
   * @returns The newly generated correlation ID
   */
  regenerate(): string {
    const correlationId = this.generateUUID();
    sessionStorage.setItem(this.STORAGE_KEY, correlationId);
    return correlationId;
  }

  /**
   * Clear the correlation ID from storage.
   * The next call to getCorrelationId() will generate a new one.
   */
  clear(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Generate a UUID v4.
   * Uses crypto.randomUUID() when available, with fallback for older browsers.
   */
  private generateUUID(): string {
    // Use native crypto.randomUUID() if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
